// Browser-side form drafts, owner-stamped and short-lived.
//
// A half-filled form survives a reload, a stray navigation or a crashed tab —
// nothing more. The draft never leaves the browser, so it must obey the same
// rules the active-role stamp obeys (utils/activeRoleStorage.ts), for the same
// reason: the whole suite for a tenant is ONE origin (modules by path,
// docs/operations/environments.md), so every module UI shares this localStorage.
// Without a stamp, signing out of one account and into another in the same
// browser would offer the first user's unsaved input to the second one.
//
// Three rules make that safe, and all three are load-bearing:
//
//   1. OWNERSHIP — a draft is readable only when the parallel `formDraftOwner:`
//      key vouches that THIS user wrote exactly THIS payload. A stamp naming a
//      different user is our own stale write from a previous account and is
//      deleted; a stamp that no longer describes what is stored means somebody
//      else overwrote the value after we stamped it, so we refuse to read it AND
//      refuse to delete it (failing closed means not using it, not destroying
//      something we cannot account for).
//   2. RETENTION — a draft older than 7 days is treated as absent and removed on
//      the next read. Unsaved personal data must not accumulate in a browser
//      indefinitely; the useful life of "I was interrupted" is days, not months.
//   3. TYPED VALUES ONLY — what gets persisted is form field values: strings,
//      numbers, booleans, null, and plain objects/arrays of those. A File, a
//      Blob, FormData, a function, a class instance or anything else exotic is
//      refused outright, and the whole write is skipped. Document content and
//      uploads never enter this store.
//
// Deliberately NOT covered, ever: council (works-council) surfaces, disability
// representation, and privacy/DSAR surfaces. Those forms simply never call
// `useFormDraft` — that is the first line of defense. `isDraftableFormId()`
// below is the second: even a future wiring mistake cannot park special-category
// or co-determination input in a browser, because the write is rejected by the
// form id itself.

import { safeGetItem, safeRemoveItem, safeSetItem } from './activeRoleStorage';

export type JsonValue = string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue };

/**
 * The record version a draft was taken against (ADR 0101). `version` is the
 * record's `updatedAt` as the form loaded it, or `null` for a record that did not
 * exist yet (an upsert singleton's first save). A draft WITH a base stores only
 * the fields the user changed against that version; a legacy draft without one
 * stores every field and restores as it always did, until it ages out.
 */
export interface FormDraftBase {
  version: string | null;
}

export interface FormDraft {
  savedAt: number;
  values: Record<string, JsonValue>;
  base?: FormDraftBase;
}

const DRAFT_PREFIX = 'formDraft:';
const DRAFT_OWNER_PREFIX = 'formDraftOwner:';

/** Unsaved input expires after a week — see rule 2 above. */
export const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

// userIds are UUIDs and the payload is JSON, so the first `|` always separates
// the two halves of a stamp (same convention as activeRoleStorage).
const STAMP_SEPARATOR = '|';

// Surfaces whose input is never parked in a browser, whatever the caller thinks.
// Works-council business is co-determined and confidential, disability
// representation is Art. 9 data, and the privacy surfaces exist to REMOVE
// personal data rather than to cache it.
const FORBIDDEN_FORM_ID = /council|disability|privacy/i;

function devWarn(message: string, ...rest: unknown[]): void {
  try {
    if (import.meta.env?.DEV) console.warn(message, ...rest);
  } catch {
    // no import.meta.env (non-Vite consumer / test runner): stay silent
  }
}

/**
 * Whether this form may keep a browser draft at all.
 *
 * Defense in depth, not the actual policy: council / disability-representation /
 * privacy forms are simply never wired to `useFormDraft`. This makes a future
 * mis-wiring inert instead of merely unlikely.
 */
export function isDraftableFormId(formId: string): boolean {
  return !FORBIDDEN_FORM_ID.test(formId || '');
}

function valueKey(tenantSlug: string, formId: string, recordId: string): string {
  return `${DRAFT_PREFIX}${tenantSlug}:${formId}:${recordId}`;
}

function ownerKeyFor(valueKeyName: string): string {
  return `${DRAFT_OWNER_PREFIX}${valueKeyName.slice(DRAFT_PREFIX.length)}`;
}

function parseDraft(raw: string): FormDraft | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return null;
  const candidate = parsed as { savedAt?: unknown; values?: unknown; base?: unknown };
  if (typeof candidate.savedAt !== 'number' || !Number.isFinite(candidate.savedAt)) return null;
  const values = candidate.values;
  if (!values || typeof values !== 'object' || Array.isArray(values)) return null;
  const draft: FormDraft = { savedAt: candidate.savedAt, values: values as Record<string, JsonValue> };
  const base = candidate.base as { version?: unknown } | undefined;
  if (base && typeof base === 'object' && !Array.isArray(base)) {
    // A base whose version is neither a string nor null is unreadable: the draft
    // then restores like a legacy one rather than claiming a base it lacks.
    if (typeof base.version === 'string' || base.version === null) {
      draft.base = { version: base.version };
    }
  }
  return draft;
}

/**
 * What the owner stamp says about the value stored under this key.
 *
 *   'mine'    — this user wrote exactly this payload.
 *   'foreign' — this exact payload was written by ANOTHER account in this browser.
 *   'unknown' — no stamp, or a stamp that no longer describes what is stored
 *               (something overwrote the value after we stamped it).
 */
function stampState(
  valueKeyName: string,
  userId: string | null | undefined,
): 'mine' | 'foreign' | 'unknown' {
  const stored = safeGetItem(valueKeyName);
  if (!stored || !userId) return 'unknown';

  const stamped = safeGetItem(ownerKeyFor(valueKeyName));
  if (!stamped) return 'unknown';

  const sep = stamped.indexOf(STAMP_SEPARATOR);
  if (sep === -1) return 'unknown';
  if (stamped.slice(sep + 1) !== stored) return 'unknown';

  return stamped.slice(0, sep) === userId ? 'mine' : 'foreign';
}

function drop(valueKeyName: string): void {
  safeRemoveItem(valueKeyName);
  safeRemoveItem(ownerKeyFor(valueKeyName));
}

/** The live draft under this key for this user, without touching storage. */
function peekDraftAt(valueKeyName: string, userId: string | null | undefined): FormDraft | null {
  if (stampState(valueKeyName, userId) !== 'mine') return null;
  const stored = safeGetItem(valueKeyName);
  const draft = stored ? parseDraft(stored) : null;
  if (!draft) return null;
  if (Date.now() - draft.savedAt > DRAFT_TTL_MS) return null;
  return draft;
}

/**
 * The draft behind this exact key, if the stamp vouches that this user wrote it
 * and it has not expired. Deletes only what it can account for: this user's own
 * dead drafts, and a previous account's leftover under this very key.
 */
function readDraftAt(valueKeyName: string, userId: string | null | undefined): FormDraft | null {
  const state = stampState(valueKeyName, userId);

  if (state === 'foreign') {
    // Our own write, for the account that used this browser before. Nobody may
    // see it, and it must not linger — it is that user's unsaved input.
    drop(valueKeyName);
    return null;
  }
  // 'unknown': no stamp, or a value something overwrote after we stamped it.
  // Not ours to read, and not ours to delete.
  if (state !== 'mine') return null;

  const draft = peekDraftAt(valueKeyName, userId);
  if (!draft) {
    // Ours by the stamp, but expired or no longer parseable. Clean it up rather
    // than keep an unusable draft around.
    drop(valueKeyName);
    return null;
  }
  return draft;
}

/**
 * This user's draft for one form + record, or null. Expired and previous-account
 * drafts are removed as a side effect of the read.
 */
export function readDraft(
  tenantSlug: string | null | undefined,
  userId: string | null | undefined,
  formId: string,
  recordId: string,
): FormDraft | null {
  if (!tenantSlug || !formId || !recordId) return null;
  return readDraftAt(valueKey(tenantSlug, formId, recordId), userId);
}

/**
 * Whether `value` is made only of the JSON primitives a form field can hold.
 * A File/Blob/FormData/function/symbol/bigint — or any class instance, Map, Set,
 * Date or DOM node — makes the whole tree unacceptable (rule 3 above).
 */
function isTypedJsonValue(value: unknown, seen: Set<object>): boolean {
  if (value === null || value === undefined) return true; // undefined is dropped by JSON.stringify
  const kind = typeof value;
  if (kind === 'string' || kind === 'boolean') return true;
  if (kind === 'number') return Number.isFinite(value as number);
  if (kind === 'function' || kind === 'symbol' || kind === 'bigint') return false;
  if (kind !== 'object') return false;

  const obj = value as object;
  if (seen.has(obj)) return false; // a cycle cannot be serialized
  seen.add(obj);
  try {
    if (Array.isArray(obj)) return obj.every((entry) => isTypedJsonValue(entry, seen));
    // Plain objects only: anything with a different prototype (File, Blob,
    // FormData, Date, Map, Set, an HTMLElement, a class instance) is refused.
    const proto = Object.getPrototypeOf(obj);
    if (proto !== Object.prototype && proto !== null) return false;
    return Object.values(obj as Record<string, unknown>).every((entry) => isTypedJsonValue(entry, seen));
  } finally {
    seen.delete(obj);
  }
}

/**
 * Park this user's current form values. Skipped entirely — never thrown — when
 * the form id is not draftable or the values are not plain typed data.
 */
export function writeDraft(
  tenantSlug: string | null | undefined,
  userId: string | null | undefined,
  formId: string,
  recordId: string,
  values: Record<string, JsonValue>,
  base?: FormDraftBase,
): void {
  if (!tenantSlug || !userId || !formId || !recordId) return;

  if (!isDraftableFormId(formId)) {
    devWarn(`[formDraftStorage] refusing to draft a non-draftable form: ${formId}`);
    return;
  }

  if (!values || typeof values !== 'object' || Array.isArray(values)) {
    devWarn(`[formDraftStorage] refusing a non-object draft payload for ${formId}`);
    return;
  }

  if (!isTypedJsonValue(values, new Set<object>())) {
    devWarn(
      `[formDraftStorage] refusing to draft ${formId}: values contain something that is not plain typed data (file, blob, function, class instance …)`,
    );
    return;
  }

  const draft: FormDraft = base ? { savedAt: Date.now(), values, base } : { savedAt: Date.now(), values };
  let serialized: string;
  try {
    serialized = JSON.stringify(draft);
  } catch {
    devWarn(`[formDraftStorage] refusing to draft ${formId}: values are not serializable`);
    return;
  }

  const key = valueKey(tenantSlug, formId, recordId);
  const stamp = `${userId}${STAMP_SEPARATOR}${serialized}`;
  safeSetItem(key, serialized);
  safeSetItem(ownerKeyFor(key), stamp);

  // The two writes are not atomic (`safeSetItem` swallows a quota error, and a
  // sibling tab can overwrite the value between them), and a value whose stamp
  // does not describe it is 'unknown' — never read, never swept, not even on
  // sign-out. Fail closed rather than leave an unattributable payload behind:
  // if the pair did not land exactly as written, take both keys away again.
  if (safeGetItem(key) !== serialized || safeGetItem(ownerKeyFor(key)) !== stamp) {
    drop(key);
    devWarn(`[formDraftStorage] draft for ${formId} did not persist consistently; removed`);
  }
}

/**
 * Drop the draft for one form + record. Unconditional by design: the caller is
 * the screen that owns the draft (saved successfully, or the user chose Discard),
 * and it must be able to clean up even when the stamp has gone stale.
 */
export function discardDraft(
  tenantSlug: string | null | undefined,
  formId: string,
  recordId: string,
): void {
  if (!tenantSlug || !formId || !recordId) return;
  drop(valueKey(tenantSlug, formId, recordId));
}

function draftKeysForTenant(tenantSlug: string): string[] {
  let keys: string[] = [];
  try {
    keys = Object.keys(localStorage);
  } catch {
    return [];
  }
  const prefix = `${DRAFT_PREFIX}${tenantSlug}:`;
  return keys.filter((key) => key.startsWith(prefix));
}

/**
 * Whether this user holds any restorable draft in this tenant.
 *
 * Same ownership test as the read path, but deliberately WITHOUT its cleanup: a
 * sweep across every key must not delete another account's leftovers as a side
 * effect of asking a question. An expired or unattributable draft is not counted
 * — the sign-out warning must only fire for input this user would actually lose.
 */
export function hasAnyDraftsForOwner(
  tenantSlug: string | null | undefined,
  userId: string | null | undefined,
): boolean {
  if (!tenantSlug || !userId) return false;
  return draftKeysForTenant(tenantSlug).some((key) => peekDraftAt(key, userId) !== null);
}

/** One draft this user would lose, named by its FORM only — see `listDraftsForOwner`. */
export interface OwnedDraftSummary {
  formId: string;
  recordId: string;
  savedAt: number;
}

/**
 * Every draft in this tenant this user owns, for the sign-out confirmation
 * (issue #364, F52) — same ownership test and the same deliberate absence of
 * cleanup as `hasAnyDraftsForOwner` (a listing must not delete anything as a
 * side effect of describing it). Newest first, so a capped list shows the
 * most relevant drafts.
 *
 * The `recordId` is returned for callers that need it (the delete path
 * does), but it is NEVER meant for display — several ids embed a record key
 * (a person's id, a contract's id), and surfacing that in a confirmation
 * dialog is exactly the mistake this type exists to keep call sites away
 * from. Render `formId` only.
 */
export function listDraftsForOwner(
  tenantSlug: string | null | undefined,
  userId: string | null | undefined,
): OwnedDraftSummary[] {
  if (!tenantSlug || !userId) return [];
  const prefix = `${DRAFT_PREFIX}${tenantSlug}:`;
  const summaries: OwnedDraftSummary[] = [];
  for (const key of draftKeysForTenant(tenantSlug)) {
    const draft = peekDraftAt(key, userId);
    if (!draft) continue;
    const rest = key.slice(prefix.length);
    const sep = rest.indexOf(':');
    if (sep === -1) continue; // malformed key — never written by valueKey()
    summaries.push({ formId: rest.slice(0, sep), recordId: rest.slice(sep + 1), savedAt: draft.savedAt });
  }
  summaries.sort((a, b) => b.savedAt - a.savedAt);
  return summaries;
}

/**
 * Delete every draft in this tenant this user owns, and nothing else. Called
 * from the DELIBERATE sign-out path only, after the user confirmed losing them
 * (an idle timeout or a forced re-auth leaves drafts alone on purpose).
 *
 * "Owns" is the stamp saying THIS user wrote exactly this payload — expiry does
 * not matter here, a dead draft of ours goes too. A draft we cannot attribute to
 * this user is left where it is, whoever it belongs to.
 */
/**
 * Delete every draft held against this workspace, whoever wrote it.
 *
 * The owner-scoped sibling below is for a sign-out: one account leaves, and
 * another's drafts are none of its business. This one is for a workspace that
 * has been erased — the records the drafts describe do not exist any more, so
 * there is nobody for whom they are still restorable.
 */
export function clearAllDraftsForTenant(tenantSlug: string | null | undefined): void {
  const slug = String(tenantSlug || '').trim();
  if (!slug) return;
  for (const key of draftKeysForTenant(slug)) {
    drop(key);
  }
}

export function clearAllDraftsForOwner(
  tenantSlug: string | null | undefined,
  userId: string | null | undefined,
): void {
  if (!tenantSlug || !userId) return;
  for (const key of draftKeysForTenant(tenantSlug)) {
    if (stampState(key, userId) !== 'mine' && !stampedForPersonaOf(key, userId)) continue;
    drop(key);
  }
}

/**
 * Whether this draft was stamped for one of this login's demo PERSONAS.
 *
 * In a demo every persona shares one real login, and time-ui owns its drafts as
 * `{userId}~{personId}` so that persona A's unsent input is never listed or
 * restored under persona B. That makes those drafts invisible to the exact-match
 * readers on purpose — `listDraftsForOwner` / `hasAnyDraftsForOwner` (the strip,
 * the guard) must not show another persona's draft. A deliberate sign-out is the
 * opposite question: the whole login leaves, so every persona's draft goes. Only
 * the clear path asks this. The stamp must still describe the stored payload.
 */
function stampedForPersonaOf(valueKeyName: string, userId: string): boolean {
  const stored = safeGetItem(valueKeyName);
  const stamped = safeGetItem(ownerKeyFor(valueKeyName));
  if (!stored || !stamped) return false;
  const sep = stamped.indexOf(STAMP_SEPARATOR);
  if (sep === -1 || stamped.slice(sep + 1) !== stored) return false;
  return stamped.slice(0, sep).startsWith(`${userId}~`);
}
